import React from "react"
import { FieldArray } from "formik"
import DeleteIcon from "@mui/icons-material/Delete"
import Grid from "@mui/material/Grid"
import makeStyles from "@mui/styles/makeStyles"
import InputLabel from "@mui/material/InputLabel"
import Typography from "@mui/material/Typography"
import UploadIcon from "@mui/icons-material/CloudUpload"
import { useSnackbar } from "notistack"
import { useTranslation } from "react-i18next"

// @ts-ignore
import Button from "../Button"
import {fileExtensionIsSupported, fileLimitTrackingText, FileUpload, overFileSizeLimit} from "./UploadMultiple.files"

const useStyles = makeStyles((theme: any) => ({
  label: {
    marginBottom: theme.spacing(1),
  },
}))

type UploadMultipleProps = {
  label: string,
  setFieldValue: (fieldName: string, fieldValue: any) => void,
  values: {files: FileUpload[]},
  fileLimit?: number,
  existingFilesSizeInBytes?: number,
}

export default function UploadMultiple(
  {
    label,
    setFieldValue,
    values,
    fileLimit,
    existingFilesSizeInBytes,
    ...props
  }: UploadMultipleProps
): JSX.Element {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation("private")

  const startAtBytes = existingFilesSizeInBytes ?? 0

  return (
    <div {...props}>
      <InputLabel className={classes.label}>
        {`${label || t("form.label.uploadAttachments")} ${fileLimitTrackingText(fileLimit, values.files, startAtBytes, t)}`}
      </InputLabel>
      <FieldArray name={'files'}>
        {() => (
          <>
            {values.files.map((file, index) => {
              const fileInputRef = React.createRef<any>()

              return file ? (
                <Grid container key={index} spacing={1} sx={{ paddingBottom: 2 }}>
                  <Grid item xs={3} container alignItems="center">
                    <Button
                      onClick={() => fileInputRef.current.click()}
                      size="small"
                      startIcon={<UploadIcon />}
                    >
                      {t("form.label.choose_file")}
                    </Button>
                  </Grid>
                  <Grid item xs={7} container alignItems="center">
                    <input
                      type="file"
                      style={{ display: "none" }}
                      onChange={(event) => {
                        const newFilesValue = [...values.files]
                        const newFiles = event.currentTarget.files
                        if (newFiles != null && newFiles.length > 0) {
                          const newFile = newFiles[0]
                          // file limit is checked here if applicable
                          if (
                            !!fileLimit
                            && overFileSizeLimit(fileLimit, newFile, newFilesValue, startAtBytes)
                          ) {
                            enqueueSnackbar(
                              t("form.label.file_limit_warning"),
                              {variant: 'warning'}
                            )
                            return
                          }
                          // check if the file extension is supported
                          if (!fileExtensionIsSupported(newFile)) {
                            enqueueSnackbar(
                              t("form.label.file_extension_warning"),
                              {variant: 'warning'}
                            )
                            return
                          }
                          newFilesValue[index] = newFiles[0]
                          setFieldValue("files", newFilesValue)
                        }
                      }}
                      ref={fileInputRef}
                    />
                    <Typography>{file.name ? file.name : t("form.label.no_file")}</Typography>
                  </Grid>
                  <Grid item xs={2} container alignItems="center" justifyContent="flex-end">
                    {values.files[0].name || values.files[1] ? (
                      <Button
                        color="secondary"
                        onClick={() => {
                          if (values.files.length < 2) {
                            values.files = [
                              {
                                name: "",
                              },
                            ]
                          } else {
                            const newFilesValue = [...values.files]
                            newFilesValue.splice(index, 1)
                            setFieldValue("files", newFilesValue)
                          }
                        }}
                        size="small"
                        variant="text"
                      >
                        <DeleteIcon />
                      </Button>
                    ) : (
                      <></>
                    )}
                  </Grid>
                </Grid>
              ) : (
                <></>
              )
            })}
            <Grid container spacing={3}>
              <Grid container item xs={12} alignItems="center" justifyContent="flex-end">
                <Button
                  color="secondary"
                  onClick={() => {
                    const newFilesValue = [...values.files]
                    newFilesValue.push({
                      name: "",
                    })
                    setFieldValue("files", newFilesValue)
                  }}
                  size="small"
                  variant="text"
                >
                  {t("form.label.another_file")}
                </Button>
              </Grid>
            </Grid>
          </>
        )}
      </FieldArray>
    </div>
  )
}
